import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRequest } from '../../../../services/api/apiService';

const ProductDetailModal = ({ product, onConfirm, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product) return;
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await getRequest(`/api/ads/shopify-oauth/products/${product.id}`);
        setDetail(res.data);
      } catch {
        setDetail({ ...product, images: [product.image_url].filter(Boolean), variants: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [product]);

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-[#221C51] text-base">Détail produit</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin"></div>
          </div>
        ) : detail ? (
          <div className="p-6">
            <div className="flex gap-6">
              {/* Images */}
              <div className="w-56 flex-shrink-0">
                <div className="w-full h-48 rounded-xl overflow-hidden bg-gray-100 mb-2">
                  {detail.images?.[activeImg]
                    ? <img src={detail.images[activeImg]} alt={detail.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🖼️</div>
                  }
                </div>
                {detail.images?.length > 1 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {detail.images.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImg(i)}
                        className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition ${
                          activeImg === i ? 'border-[#221C51]' : 'border-gray-200'
                        }`}
                      >
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#221C51] text-lg leading-tight">{detail.title}</p>
                {detail.vendor && <p className="text-xs text-gray-400 mt-0.5">{detail.vendor}</p>}
                <p className="text-[#221C51] font-bold text-xl mt-2">
                  {detail.price ? `${detail.price} MAD` : '—'}
                </p>
                {detail.description && (
                  <p className="text-xs text-gray-600 mt-3 leading-relaxed line-clamp-4">{detail.description}</p>
                )}
                {detail.product_url && (
                  <a
                    href={detail.product_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-2 text-xs text-[#221C51] underline truncate max-w-full"
                  >
                    {detail.product_url}
                  </a>
                )}

                {/* Variants */}
                {detail.variants?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-500 mb-2">VARIANTES</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.variants.map((v) => (
                        <span key={v.id} className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
                          {v.title} — {v.price} MAD
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition"
          >
            Annuler
          </button>
          <button
            onClick={() => { onConfirm(detail || product); onClose(); }}
            className="px-5 py-2 text-sm text-white bg-[#221C51] rounded-lg hover:bg-[#2e265e] transition font-semibold"
          >
            Choisir ce produit →
          </button>
        </div>
      </div>
    </div>
  );
};

const ShopifyProductPicker = ({ selected, onSelect }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [previewProduct, setPreviewProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getRequest('/api/ads/shopify-oauth/products');
        setProducts(response.data || []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin mb-4"></div>
        <p className="text-sm">Chargement des produits Shopify…</p>
      </div>
    );
  }

  const filtered = products.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase())
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <p className="text-sm">Impossible de charger les produits Shopify.</p>
        <Link to="/dashboard/connected-platforms" className="mt-2 text-xs text-[#221C51] underline">
          Vérifier la connexion Shopify
        </Link>
      </div>
    );
  }

  return (
    <div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher un produit…"
        className="w-full mb-5 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#221C51]/50"
      />

      {products.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-sm">Aucun produit trouvé dans votre boutique Shopify.</p>
          <Link to="/dashboard/connected-platforms" className="mt-2 text-xs text-[#221C51] underline">
            Connecter Shopify
          </Link>
        </div>
      )}

      {filtered.length === 0 && products.length > 0 && (
        <p className="text-center text-sm text-gray-400 py-12">
          Aucun produit pour « {search} »
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((product) => (
          <button
            key={product.id}
            type="button"
            onClick={() => setPreviewProduct(product)}
            className={`text-left rounded-xl border-2 overflow-hidden transition-all ${
              selected?.id === product.id
                ? 'border-[#221C51] shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <div className="relative">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="w-full h-32 object-cover"
                />
              ) : (
                <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-300 text-3xl">
                  🖼️
                </div>
              )}
              {selected?.id === product.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#221C51] rounded-full flex items-center justify-center shadow">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              )}
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2">
                {product.title}
              </p>
              {product.price && (
                <p className="text-xs text-[#221C51] font-bold mt-1">{product.price} MAD</p>
              )}
            </div>
          </button>
        ))}
      </div>

      <ProductDetailModal
        product={previewProduct}
        onConfirm={(p) => { onSelect(p); setPreviewProduct(null); }}
        onClose={() => setPreviewProduct(null)}
      />
    </div>
  );
};

export default ShopifyProductPicker;
